import type { CollectionPage, ListItem, Product as ProductSchema, WithContext } from 'schema-dts';

import { getProductUrl } from '@libs/algolia';
import type { Product } from '@libs/common-next';
import {
    buildOfferMerchantListingSchema,
    isProductHitEligibleForMerchantListingSchema,
    JsonLd,
} from '@libs/common-next';
import { LOCALIZED } from '@libs/core';

import type { ProductGridSeoProps } from './types';

const IN_STOCK = 'https://schema.org/InStock';
const OUT_OF_STOCK = 'https://schema.org/OutOfStock';

export default function ProductGridSeo({ seoMetadata, products }: ProductGridSeoProps) {
    const { metaTitle, metaDescription } = seoMetadata;
    const merchantListingSchema = buildOfferMerchantListingSchema();

    const itemListElement = products
        .filter(
            (product?: Product) =>
                // Filter out products with nullish rawProductHit or missing price or priceData
                product?.rawProductHit?.priceData || product?.rawProductHit?.price
        )
        .map((product: Product, index: number): ListItem => {
            const price = String(product?.rawProductHit?.priceData?.current ?? product?.rawProductHit?.price?.number);

            const productSchema: ProductSchema = {
                '@type': 'Product',
                name: product.title,
                image: `https://s7d2.scene7.com/is/image/PetSmart/${product.id}`,
                description: product.title,
                sku: String(product.id),
                ...(product.brand && {
                    brand: {
                        '@type': 'Brand',
                        name: product.brand,
                    },
                }),
                offers: {
                    '@type': 'Offer',
                    priceCurrency: LOCALIZED.currencyCode,
                    price,
                    priceValidUntil: '9999-12-31T00:00:00.000Z',
                    availability: product.rawProductHit.in_stock === false ? OUT_OF_STOCK : IN_STOCK,
                    url: `${LOCALIZED.hostname}${getProductUrl(product.rawProductHit)}`,
                    ...(isProductHitEligibleForMerchantListingSchema(product.rawProductHit)
                        ? merchantListingSchema
                        : {}),
                },
            };

            // Only add aggregateRating object if we have one or more reviews
            if (product.rawProductHit.bvReviewCount && product.rawProductHit.bvAverageRating) {
                productSchema.aggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: product.rawProductHit.bvAverageRating,
                    reviewCount: product.rawProductHit.bvReviewCount,
                    bestRating: '5',
                    worstRating: '1',
                };
            }

            return {
                '@type': 'ListItem',
                position: index + 1,
                item: productSchema,
            };
        });

    const jsonLd: WithContext<CollectionPage> = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: metaTitle,
        description: metaDescription,
        mainEntity: {
            '@type': 'ItemList',
            itemListElement,
        },
    };

    return (
        <JsonLd key="plp-collections-seo" id="plp-collections-seo" data-testid="plp-collections-seo" json={jsonLd} />
    );
}
